<template>
  <SimpleTextPopup
    :title="$t('offlineDeposit')"
    v-model="value"
    @popupClose="hideDialog"
    :hideRgtBtn="true"
    :outsideScrollToBottom="outsideScrollToBottom"
    large
  >
    <template v-slot:input>
      <v-layout row wrap>
        <v-flex xs3 lg1 v-if="isOrg">
          <v-btn color="primary" @click="addNewOffline()">
            <v-icon>add</v-icon> {{ $t('offlineDeposit') }}
          </v-btn>
        </v-flex>
      </v-layout>
      <v-tabs color="transparent" v-model="tab" show-arrows>
        <v-tabs-slider color="primary"></v-tabs-slider>
        <v-tab v-for="(tabItem) in tabs" :key="tabItem">
          <span class="tabItem"> {{ $t(tabItem.toLowerCase()) }} </span>
        </v-tab>
        <v-tabs-items v-model="tab" touchless>
          <v-tab-item key="active">
            <v-data-table
              v-if="tabs[tab] === 'Active'"
              :headers="headers"
              :items.sync="offlineDeposits"
              :search="offlineSearch"
              class="elevation-2 myDense"
              :rows-per-page-items="rowPerPage"
            >
              <template v-slot:items="props">
                <td>
                  <v-layout>
                    <span>{{ props.item.id}}</span>
                    <v-tooltip top>
                      <template v-slot:activator="data">
                        <div v-on="data.on"><v-icon color="primary" small v-if="props.item.isAdjustedBalance">paid</v-icon></div>
                      </template>
                      <span>{{ $t('adjusted') }}</span>
                    </v-tooltip>
                  </v-layout>
                </td>
                <td>{{ props.item.vendorName}}</td>
                <td>{{props.item.currencyType}}{{ props.item.amount}}</td>
                <td>{{ props.item.orgName}}</td>
                <td>{{ $t(props.item.offlineType) }}</td>
                <td
                  style="
                    word-break: break-word;
                    max-width: 300px;
                  "
                >
                  {{ props.item.note }}
                </td>
                <td>{{ props.item.status }}</td>
                <td>{{ toTimestampString(props.item.createTime) }}</td>
                <td text-xs-center>
                  <v-layout row>
                    <v-flex>
                      <v-btn
                        dark
                        color="primary"
                        flat
                        @click.stop="showCommentsDialog(props.item)"
                        >{{ $t('open') }}
                        <v-badge
                          class="OfflineOpenBadge"
                          :value="checkNewOfflineDeposit(props.item)"
                          color="red"
                        >
                          <template v-slot:badge>
                            <span class="badge">{{ $t('new') }}</span>
                          </template>
                        </v-badge>
                      </v-btn>
                    </v-flex>
                    <v-flex>
                      <v-btn
                        dark
                        v-if="!isOrg"
                        color="primary"
                        flat
                        @click.stop="showAdjustDialog(props.item)"
                        >{{ $t('adjust') }}
                      </v-btn>
                    </v-flex>
                    <v-flex v-if="!activeWarehouse && !isAdmin">
                       <v-btn flat :disabled="props.item.isAdjustedBalance" color="primary" @click.stop="cancelOfflineDeposit(props.item.id)">{{$t('cancel')}}</v-btn>
                    </v-flex>
                  </v-layout>
                </td>
              </template>
            </v-data-table>
          </v-tab-item>
          <v-tab-item key="history">
            <PaginationController
              v-if="tabs[tab] === 'History'"
              v-model="offlineDepositsHistory"
              :refresh="paginationRefreshTrigger"
              getDataActionName="getHistoryOfflineDeposit"
              :historyLimit="historyLimit"
              :outsideScrollToBottom="popupToBottom"
            >
              <template v-slot:dataTable>
                <v-data-table
                  :headers="headers"
                  :items="offlineDepositsHistory"
                  hide-actions
                  disable-initial-sort
                >
                  <template v-slot:items="props">
                    <td>{{ props.item._key}}</td>
                    <td>{{ props.item.vendorName}}</td>
                    <td>{{props.item.currencyType}}{{ props.item.amount}}</td>
                    <td>{{ props.item.orgName}}</td>
                    <td>{{ $t(props.item.offlineType) }}</td>
                    <td
                      style="
                        word-break: break-word;
                        max-width: 300px;
                      "
                    >
                      {{ props.item.note }}
                    </td>
                    <td>{{ props.item.status }}</td>
                    <td>{{ toTimestampString(props.item.createTime) }}</td>
                    <td>{{ toTimestampString(props.item.lastModifiedTime) }}</td>
                    <td text-xs-center>
                      <v-layout row>
                        <v-flex>
                          <v-btn
                            dark
                            color="primary"
                            flat
                            @click.stop="showCommentsDialog(props.item)"
                            >{{ $t('open') }}
                          </v-btn>
                        </v-flex>
                      </v-layout>
                    </td>
                  </template>
                </v-data-table>
              </template>
            </PaginationController>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
      <ChatRoom
        v-model="commentsDialog"
        :title="$t('caseOfflineDeposit')"
        v-if="commentsDialog"
        :lastReadTime="offlineLastRead"
        enableFileUpload
        :docPath="['depositRequests', OfflineDepositKey]"
        :comments="offlineComments"
        :readOnly="(offlineEdit && offlineEdit.status !== 'open') || offlineEdit.isRemoved"
        :nickName="(activeWarehouse || isAdmin) ? offlineAgent : ''"
        ref="chatRoom"
      >
        <template v-slot:issue>
          <v-card raised>
            <v-card-text>
              <v-layout class="cardTitle">{{ $t('requestID') }}:{{ offlineEdit.id || offlineEdit._key }}</v-layout>
              <v-layout class="cardTitle" style="overflow-wrap: break-word">
                {{ $t('createTime') }}: {{ toTimestampString(offlineEdit.createTime) }}
              </v-layout>
              <v-layout v-if="activeWarehouse" class="cardTitle" style="overflow-wrap: break-word">
                {{ $t('organization') }}: {{ offlineEdit.orgName }}
              </v-layout>
              <v-layout v-else class="cardTitle" style="overflow-wrap: break-word">{{ $t('vendor') }}: {{ offlineEdit.vendorName }}</v-layout>
              <v-layout class="cardTitle">{{ $t('amount') }}: {{ offlineEdit.currencyType }}{{ offlineEdit.amount }}</v-layout>
              <v-layout class="cardTitle">{{ $t('offlineType') }}:{{ offlineEdit.offlineType }}</v-layout>
              <v-layout class="cardTitle" style="overflow-wrap: break-word">{{ $t('note') }}:{{ offlineEdit.note }}</v-layout>
              <v-layout class="cardTitle">{{ $t('status') }}:{{ offlineEdit.status }}</v-layout>
              <v-layout class="cardTitle" style="overflow-wrap: break-word">
                {{ $t('lastModifiedTime') }}: {{ toTimestampString(offlineEdit.lastModifiedTime || offlineEdit.createTime) }}
              </v-layout>
              <v-layout v-if="!!offlineEdit.transactionDocKeys" class="cardTitle">
                <div>
                  {{ $t('adjustHistory') }}:
                </div>
                <v-flex style="margin-top: -14px;">
                  <div v-for="(docKey,index) in offlineEdit.transactionDocKeys" :key="index">
                    <v-flex  style="cursor:pointer;text-decoration:underline;" @click.stop="openTransactionPopup(docKey, offlineEdit)">{{$t('adjust')}}{{index+1}}</v-flex>
                  </div>
                </v-flex>
              </v-layout>
              <v-layout v-if="(offlineEdit.uploadFiles || []).length" row wrap>
                <FsFileChip
                  v-for="(file, index) in offlineEdit.uploadFiles"
                  :file="file"
                  :key="'file' + index"
                  width="100%"
                  aspect-ratio="1.7"
                  class="ma-2"
                />
              </v-layout>
            </v-card-text>
          </v-card>
        </template>
        <template v-if="activeWarehouse || isAdmin" v-slot:operation>
          <v-layout row no-wrap align-baseline>
            <v-flex>
              <v-btn
                v-if="offlineEdit.status == 'open' && !offlineEdit.isRemoved"
                small
                color="primary"
                @click.stop="ProcessOfflineRequests('success')"
                >{{ $t('confirm') }}</v-btn
              >
            </v-flex>
            <v-flex>
              <v-btn
                v-if="offlineEdit.status == 'open' && !offlineEdit.isRemoved"
                small
                color="primary"
                @click.stop="ProcessOfflineRequests('canceled')"
                >{{ $t('cancel') }}</v-btn
              >
            </v-flex>
          </v-layout>
        </template>
      </ChatRoom>
      <FormSubmitPopup
        :title="$t('addDeposit')"
        v-model="newOfflineDialog"
        v-if="newOfflineDialog"
        :rightButtonText="$t('submit')"
        @popupClose="()=> newOfflineDialog=false"
        :rightMethod="submitOfflineDeposit"
        :rightButtonDisabled="!isEmpty"
        hasAlert
        medium
      >
        <template v-slot:input>
          <v-container class="offline">
            <v-layout ref="form" justify-space-between>
              <v-flex md5 xl4>
                <v-radio-group v-model="offlineType" row>
                  <v-radio :label="$t('check')" value="check"></v-radio>
                  <v-radio label="ACH" value="ach"></v-radio>
                  <v-radio :label="$t('other')" value="other"></v-radio>
                </v-radio-group>
              </v-flex>

              <v-flex md4 xl4 class="AmountInput" >
                <v-text-field
                  ref='focus'
                  id="balance_amount"
                  :label="$t('amount')"
                  :hint="$t('changeCurrency')"
                  type="number"
                  :rules="amountRules"
                  v-model.number="amount"
                  :disabled="submitting"
                >
                  <template v-slot:prepend-inner>
                    <span style="cursor: pointer" @click="transactionCurrency(currency)">{{currencyType}}</span>
                  </template>
                </v-text-field>
              </v-flex>
            </v-layout>
            <v-layout>
              <v-textarea
                :label="$t('note')"
                v-model="note"
                outline
                :rules="noteRules"
                :counter="200"
                auto-grow
              ></v-textarea>
            </v-layout>
            <v-layout>
              <UploadFileWidget
                v-model="uploadFiles"
                ref="fileUploader"
                type="file"
                :maxFiles="9"
              /></v-layout>
          </v-container>
        </template>
      </FormSubmitPopup>
      <AdjustBalancePopup
      v-if="adjustBalanceDialog"
      v-model="adjustBalanceDialog"
      :adjustment="adjustBalance"
      :actionFunc="addAdjustBalance"/>
      <TransactionDetailsPopup
      v-if="showTransactionPopup"
      v-model="showTransactionPopup"
      :transaction="transaction"/>
    </template>
  </SimpleTextPopup>
</template>

<script>
import SimpleTextPopup from './SimpleTextPopup'
import BillingTransactionTable from './BillingTransactionTable'
import { timeTools, toMoney, checkRules, splitKeyword } from '@/utils/tools'
import FsFileChip from '@/components/FsFileChip'
import ChatRoom from '@/components/ChatRoom'
import FormSubmitPopup from './FormSubmitPopup'
import UploadFileWidget from './UploadFileWidget'
import PaginationController from './PaginationController'
import AdjustBalancePopup from './AdjustBalancePopup'
import TransactionDetailsPopup from './TransactionDetailsPopup'

export default {
  name: 'OfflineDepositPopup',
  components: {
    SimpleTextPopup,
    BillingTransactionTable,
    ChatRoom,
    FsFileChip,
    UploadFileWidget,
    FormSubmitPopup,
    PaginationController,
    AdjustBalancePopup,
    TransactionDetailsPopup
  },
  mixins: [timeTools, checkRules],
  data () {
    return {
      tab: 0,
      tabs: ['Active', 'History'],
      offlineEdit: {},
      popupToBottom: false,
      offlineSearch: '',
      rowPerPage: [30, 50, {text: this.$t('all'), value: -1}],
      commentsDialog: false,
      newOfflineDialog: false,
      amount: null,
      offlineType: 'check',
      note: '',
      uploadFiles: [],
      OfflineDepositDialog: true,
      amountRules: [
        this.fieldIsRequired('Amount')
      ],
      noteRules: [
        this.fieldCharLimit('Note', 200)
      ],
      submitting: false,
      paginationRefreshTrigger: false,
      historyLimit: 15,
      offlineDepositsHistory: [],
      currency: true,
      isProcessingOfflineRequest: false,
      adjustBalanceDialog: false,
      adjustBalance: {
        amount: 0,
        note: '',
        type: 'balance',
        keywords: ''
      },
      showTransactionPopup: false,
      transaction: {}
    }
  },
  computed: {
    tenantsWithBillings () {
      return this.$store.getters.tenantsWithBillings || []
    },
    currencyType () {
      return this.currency ? '$' : '¥'
    },
    isEmpty () {
      return (this.amount > 0)
    },
    offlineDeposits () {
      return this.$store.getters.offlineDeposits || []
    },
    OfflineDepositKey () {
      return (this.offlineEdit && this.offlineEdit.id)
    },
    headers () {
      if (this.tab === 0) {
        return [
          { text: this.$t('requestID'), align: 'center', sortable: false, value: 'id', width: '10%' },
          { text: this.$t('vendor'), align: 'left', sortable: false, value: 'vendorKey' },
          { text: this.$t('amount'), align: 'left', sortable: false, value: 'amount' },
          { text: this.$t('organization'), align: 'left', sortable: false, value: 'orgName' },
          { text: this.$t('type'), value: 'type', align: 'left', sortable: false },
          { text: this.$t('note'), value: 'note', align: 'left', sortable: false },
          { text: this.$t('status'), value: 'status', align: 'left', sortable: false },
          { text: this.$t('createTime'), value: 'createTime', align: 'left', sortable: false },
          { text: this.$t('actions'), value: '', align: 'center', sortable: false, width: '6%' }
        ]
      } else {
        return [
          { text: this.$t('requestID'), align: 'center', sortable: false, value: 'id', width: '10%' },
          { text: this.$t('vendor'), align: 'left', sortable: false, value: 'vendorKey' },
          { text: this.$t('amount'), align: 'left', sortable: false, value: 'amount' },
          { text: this.$t('organization'), align: 'left', sortable: false, value: 'orgName' },
          { text: this.$t('type'), value: 'type', align: 'left', sortable: false },
          { text: this.$t('note'), value: 'note', align: 'left', sortable: false },
          { text: this.$t('status'), value: 'status', align: 'left', sortable: false },
          { text: this.$t('createTime'), value: 'createTime', align: 'left', sortable: true },
          { text: this.$t('lastModifiedTime'), value: 'lastModifiedTime', align: 'left', sortable: true },
          { text: this.$t('actions'), value: '', align: 'center', sortable: false, width: '6%' }
        ]
      }
    },
    tenantName () {
      return this.$store.getters
    },
    closedHeaders () {
      return this.headers.filter(({text}) => text !== 'Status')
    },
    warehouses () {
      return [
        ...this.$store.getters.allPreferredWarehouses,
        {
          warehouseName: 'System',
          warehouseKey: 'system',
          orgId: 'self'
        }
      ]
    },
    activeWarehouse () {
      return this.$store.getters.activeWarehouse
    },
    isOrg () {
      return !!this.$store.getters.activeOrganization
    },
    offlineLastRead () {
      return (this.offlineEdit
        && this.offlineEdit[`lastRead_${this.$store.getters.uid}`]) || new Date('2018-1-1')
    },
    commentsLastRead () {
      return (this.offlineEdit
        && this.offlineEdit[`lastRead_comments_${this.$store.getters.uid}`]) || new Date('2018-1-1')
    },
    offlineComments () {
      return (this.offlineEdit && this.offlineEdit.comments) || []
    },
    offlineAgent () {
      return (this.offlineEdit && this.offlineEdit.agentName) ||
        (this.$store.getters.userExtra && this.$store.getters.userExtra.name) ||
        'System'
    },
    user () {
      return this.$store.getters.user
    },
    isAdmin () {
      return !!(this.$store.getters.user || {}).isAdmin
    }
  },
  watch: {
    newOfflineDialog (val) {
      if (!val) return
      requestAnimationFrame(() => {
        return this.$refs.focus.focus()
      })
    },
    tab (cur, old) {
      if (cur === 1) {
        this.paginationRefreshTrigger = !this.paginationRefreshTrigger
      }
    },
    commentsDialog (val) {
      if (!val) {
        this.offlineEdit = {}
      }
    },
    offlineDeposits (cur, old) {
      if (this.offlineEdit && this.offlineEdit.id && !this.isProcessingOfflineRequest) {
        let newInEdit = this.offlineDeposits.find(({id}) => id === this.offlineEdit.id)
        if (newInEdit === undefined) {
          this.offlineEdit.isRemoved = true
          alert(this.$t('EditItemRemovedAlert'))
        } else {
          this.offlineEdit = newInEdit
        }
      }
    }
  },
  methods: {
    async openTransactionPopup (docKey, item) {
      let transaction = await this.$store.dispatch('getAdminOrWarehouseTransaction', {docKey, item})
      !transaction && this.$store.dispatch('showToast', { info: this.$t('adjustInfoMissing'), level: 'error' })
      this.transaction = transaction
      this.showTransactionPopup = true
    },
    async addAdjustBalance () {
      this.isAdmin ? await this.adjustSystemBalance() : await this.adjustWarehouseBalance()
    },
    adjustSystemBalance () {
      let { note, amount, type, tenantKey, keywords, orgName, id, offlineType, attachments = [] } = this.adjustBalance
      let payload = {
        offlineDepositDocId: id,
        offlineType,
        clientKey: tenantKey,
        clientName: orgName,
        amount: amount,
        note,
        type,
        keywords: splitKeyword(keywords),
        attachments
      }
      return this.$store.dispatch('adjustSystemBalance', payload)
    },
    adjustWarehouseBalance () {
      let { note, amount, tenantKey, id, offlineType, orgName, attachments = [], keywords } = this.adjustBalance
      let { balance = 0 } = this.tenantsWithBillings.find(tenant => tenant.key === tenantKey) || {}
      let payload = {
        offlineDepositDocId: id,
        clientName: orgName,
        offlineType,
        note,
        amount,
        type: 'adjust',
        tenantKey,
        balance,
        attachments,
        keywords: splitKeyword(keywords)
      }
      return this.$store.dispatch('adjustWarehouseBalance', payload)
    },
    showAdjustDialog (item) {
      const { id } = item
      Object.assign(this.adjustBalance, item)
      this.adjustBalance.note = `Offline Deposit: ${id}`
      this.adjustBalanceDialog = true
    },
    cancelOfflineDeposit (id) {
      if (!confirm(this.$t('cancelDeposit'))) return
      let payload = {
        status: 'canceled',
        docKey: id,
        clientName: this.$store.getters.tenantLimitedInfo.name
      }
      this.isProcessingOfflineRequest = true
      return this.$store.dispatch('updateOfflineDeposits', payload)
        .finally(() => {
          this.isProcessingOfflineRequest = false
        })
    },
    transactionCurrency () {
      this.currency = !this.currency
    },
    submitOfflineDeposit () {
      let uploadFiles = Object.assign([], this.uploadFiles)
      console.log(this.$store.getters.isModeLabel)
      let payload = this.currentWarehouse.orgId ? {
        currencyType: this.currencyType,
        uploadFiles: uploadFiles,
        amount: toMoney(this.amount),
        status: 'open',
        tenantKey: this.$store.getters.activeOrganization || '',
        vendorName: this.currentWarehouse.warehouseName,
        vendorKey: this.currentWarehouse.warehouseKey,
        offlineType: this.offlineType,
        createTime: new Date(),
        note: this.note,
        orgName: this.$store.getters.tenantLimitedInfo.name || '',
        isModeLabel: this.$store.getters.isModeLabel
      } : {
        currencyType: this.currencyType,
        uploadFiles: uploadFiles,
        amount: toMoney(this.amount),
        status: 'open',
        tenantKey: this.$store.getters.activeOrganization || '',
        vendorName: this.currentWarehouse.warehouseName,
        vendorKey: 'system',
        offlineType: this.offlineType,
        createTime: new Date(),
        note: this.note,
        orgName: this.$store.getters.tenantLimitedInfo.name || '',
        isModeLabel: this.$store.getters.isModeLabel
      }
      return this.$store.dispatch('addOfflineDeposits', payload)
        .then(result => {
          this.$store.dispatch('showToast', { info: result, level: 'success' })
          this.amount = null
          this.offlineType = 'check'
          this.note = ''
          this.uploadFiles = []
          this.newOfflineDialog = false
        })
        .catch(error => {
          this.$store.dispatch('showToast', { info: `${this.$t('paymentFailed')}: ${error}`, level: 'error' })
          console.error('payment failed: ', error)
          this.$emit('change', false)
        })
    },
    addNewOffline () {
      this.newOfflineDialog = true
    },
    ProcessOfflineRequests (status) {
      this.isProcessingOfflineRequest = true
      if (status === 'success') {
        if (!confirm(this.$t('confirmDeposit'))) return
      } else {
        if (!confirm(this.$t('cancelDeposit'))) return
      }
      let payload = {
        status,
        docKey: this.OfflineDepositKey,
        clientName: this.offlineEdit.vendorName
      }
      return this.$store.dispatch('updateOfflineDeposits', payload)
        .then((res) => {
          this.$store.dispatch('showToast', { info: res, level: 'success' })
          this.commentsDialog = false
        })
        .finally(() => {
          this.isProcessingOfflineRequest = false
        })
    },
    hideDialog () {
      this.$emit('input', false)
      this.$emit('isHidePopup')
    },
    checkNewOfflineDeposit (request) {
      let lastReadTime = request[`lastRead_${this.$store.getters.uid}`]
      let hasOnlyInitialComment = request.comments && request.comments.length === 1 && request.comments[0].initialComment
      let lastMessageTime = request.comments && request.comments.length > 0 && request.comments.slice(-1)[0].createTime

      return !hasOnlyInitialComment && !!lastMessageTime && (!lastReadTime || lastReadTime < lastMessageTime)
    },
    getOrgId (tenantKey = '') {
      return this.$store.getters.organizationKeyToId.get(tenantKey) || ''
    },
    showCommentsDialog (item) {
      this.offlineEdit = item
      if (this.offlineEdit.agentUid) {
        this.agentName = this.offlineEdit.agentName
      } else {
        this.agentName = ''
      }
      this.commentsDialog = true
    },
    outsideScrollToBottom (flag) {
      this.popupToBottom = flag
    }
  },
  props: {
    value: Boolean,
    currentWarehouse: Object
  }
}
</script>

<style>
.OfflineOpenBadge .v-badge__badge {
  font-size: 10px;
  left: -6px;
  top: -23px;
}
</style>



// WEBPACK FOOTER //
// src/components/OfflineDepositPopup.vue